import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineChartBar,
  HiOutlineDocumentText,
  HiOutlineClipboardDocumentList,
  HiOutlineSquares2X2,
  HiOutlineListBullet,
  HiOutlineCurrencyDollar,
  HiOutlineExclamationTriangle,
  HiOutlineBuildingOffice2,
  HiOutlineUserGroup,
} from "react-icons/hi2";
import { usePermissions } from "../hooks/usePermissions";
import { useNavigationAccess } from "../navigation/useNavigationAccess";
import "./ReportsDashboard.css";

const ReportsDashboard = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { ctx } = useNavigationAccess();
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [selectedModule, setSelectedModule] = useState("All");

  const canViewReports = hasPermission("Reports.View");
  const subscribedModules = ctx.subscribedModules;

  const isModuleSubscribed = (code) => {
    if (!subscribedModules) return true;
    if (!code) return true;
    return subscribedModules.has(String(code).toUpperCase());
  };

  const moduleTabsAll = [
    { id: "All", label: "All Reports", moduleCode: null },
    { id: "OEE", label: "OEE", moduleCode: "OEE_PRODUCTION" },
    { id: "PM", label: "PM", moduleCode: "MACHINE_HEALTH" },
    { id: "Work Order", label: "Work Order", moduleCode: "WORK_ORDER" },
    { id: "Hazard", label: "Hazard", moduleCode: "HAZARD" },
    { id: "Safety", label: "Safety", moduleCode: "SAFETY_OBS" },
    { id: "Inventory", label: "Inventory", moduleCode: "SPARE_INVENTORY" },
  ];

  const moduleTabs = moduleTabsAll.filter(tab => isModuleSubscribed(tab.moduleCode));

  const allReports = [
    {
      title: "OEE Summary",
      description:
        "Availability, Performance, Quality and OEE% per machine per day.",
      path: "/home/reports/oee-summary",
      icon: <HiOutlineChartBar />,
      featureCode: "OEE_ANALYTICS",
      module: "OEE",
    },
    {
      title: "Loss Report",
      description: "Detailed breakdown of losses and downtime reasons.",
      path: "/home/reports/oee-loss",
      icon: <HiOutlineClipboardDocumentList />,
      featureCode: "OEE_ANALYTICS",
      module: "OEE",
    },
    {
      title: "Loss Trends Report",
      description: "Loss occurrences and frequency trends over time.",
      path: "/home/reports/oee-loss-trends",
      icon: <HiOutlineChartBar />, // Matches sub-module icon
      featureCode: "OEE_ANALYTICS",
      module: "OEE",
    },
    {
      title: "Financial Loss Report",
      description: "Financial impact analysis of production losses.",
      path: "/home/reports/oee-financial-loss",
      icon: <HiOutlineCurrencyDollar />,
      featureCode: "OEE_ANALYTICS",
      module: "OEE",
    },
    {
      title: "MTBR Report",
      description: "Mean Time Between Repairs performance analysis.",
      path: "/home/reports/mtbr",
      icon: <HiOutlineDocumentText />,
      featureCode: "WORK_ORDER_MODULE",
      module: "Work Order",
    },
    {
      title: "MTBF Report",
      description: "Mean Time Between Failures performance analysis.",
      path: "/home/reports/mtbf",
      icon: <HiOutlineDocumentText />,
      featureCode: "WORK_ORDER_MODULE",
      module: "Work Order",
    },
    {
      title: "PM Schedule Compliance",
      description: "Track compliance with planned maintenance schedules.",
      path: "/home/reports/pm-schedule-compliance",
      icon: <HiOutlineDocumentText />,
      featureCode: "PLANNED_MAINTENANCE",
      module: "PM",
    },
    {
      title: "PM Overdue",
      description: "View and manage overdue planned maintenance tasks.",
      path: "/home/reports/pm-overdue",
      icon: <HiOutlineDocumentText />,
      featureCode: "PLANNED_MAINTENANCE",
      module: "PM",
    },
    {
      title: "PM Standards",
      description: "View and export detailed records of individual PM tasks.",
      path: "/home/reports/individual-tasks",
      icon: <HiOutlineClipboardDocumentList />,
      featureCode: "PLANNED_MAINTENANCE",
      module: "PM",
    },
    {
      title: "PM Standards Tracker",
      description: "Visual matrix tracking of PM Standards status.",
      path: "/home/reports/pm-standards-tracker",
      icon: <HiOutlineChartBar />,
      featureCode: "PLANNED_MAINTENANCE",
      module: "PM",
    },
    {
      title: "Low Stock Report",
      description: "Parts where current stock is below minimum stock level.",
      path: "/home/inventory/reports/low-stock",
      icon: <HiOutlineExclamationTriangle />,
      featureCode: null,
      module: "Inventory",
    },
    {
      title: "Overstock Report",
      description: "Parts with excess stock exceeding maximum limits.",
      path: "/home/inventory/reports/overstock",
      icon: <HiOutlineChartBar />,
      featureCode: null,
      module: "Inventory",
    },
    {
      title: "Inventory Valuation",
      description: "Financial value of current inventory stock.",
      path: "/home/inventory/reports/valuation",
      icon: <HiOutlineCurrencyDollar />,
      featureCode: null,
      module: "Inventory",
    },
    {
      title: "Stock Movement Report",
      description: "Transaction history for purchases, issues and adjustments.",
      path: "/home/inventory/reports/movement",
      icon: <HiOutlineDocumentText />,
      featureCode: null,
      module: "Inventory",
    },
    {
      title: "Hazard Category Report",
      description: "Distribution of hazards by category.",
      path: "/home/reports/hazard-reports",
      state: { activeTab: "category" },
      icon: <HiOutlineChartBar />,
      featureCode: "HAZARD_REPORT",
      module: "Hazard",
    },
    {
      title: "Hazard Severity Report",
      description: "Risk assessment distribution of hazards.",
      path: "/home/reports/hazard-reports",
      state: { activeTab: "severity" },
      icon: <HiOutlineExclamationTriangle />,
      featureCode: "HAZARD_REPORT",
      module: "Hazard",
    },
    {
      title: "Hazard Action Report",
      description: "Track immediate actions taken for hazards.",
      path: "/home/reports/hazard-reports",
      state: { activeTab: "action" },
      icon: <HiOutlineDocumentText />,
      featureCode: "HAZARD_REPORT",
      module: "Hazard",
    },
    {
      title: "Hazard Department Report",
      description: "Analyze hazards distribution by departments.",
      path: "/home/reports/hazard-reports",
      state: { activeTab: "department" },
      icon: <HiOutlineBuildingOffice2 />,
      featureCode: "HAZARD_REPORT",
      module: "Hazard",
    },
    {
      title: "SBO Summary Report",
      description: "Overall statistics for safety behavior observations.",
      path: "/home/safety-observation/reports",
      state: { activeTab: "summary" },
      icon: <HiOutlineChartBar />,
      featureCode: "SAFETY_OBSERVATION_MODULE",
      module: "Safety",
    },
    {
      title: "SBO Unsafe Behavior",
      description: "Detailed analysis of unsafe safe behaviors and conditions.",
      path: "/home/safety-observation/reports",
      state: { activeTab: "unsafe" },
      icon: <HiOutlineExclamationTriangle />,
      featureCode: "SAFETY_OBSERVATION_MODULE",
      module: "Safety",
    },
    {
      title: "SBO Action Tracking",
      description: "Track status of corrective actions from safety observations.",
      path: "/home/safety-observation/reports",
      state: { activeTab: "action" },
      icon: <HiOutlineClipboardDocumentList />,
      featureCode: "SAFETY_OBSERVATION_MODULE",
      module: "Safety",
    },
    {
      title: "SBO Category Report",
      description: "Distribution of safety observations by category.",
      path: "/home/safety-observation/reports",
      state: { activeTab: "category" },
      icon: <HiOutlineChartBar />,
      featureCode: "SAFETY_OBSERVATION_MODULE",
      module: "Safety",
    },
    {
      title: "SBO Area Report",
      description: "Safety observations by area and department.",
      path: "/home/safety-observation/reports",
      state: { activeTab: "area" },
      icon: <HiOutlineBuildingOffice2 />,
      featureCode: "SAFETY_OBSERVATION_MODULE",
      module: "Safety",
    },
    {
      title: "SBO Observer Performance",
      description: "Analyze safety observer participation and performance.",
      path: "/home/safety-observation/reports",
      state: { activeTab: "observer" },
      icon: <HiOutlineUserGroup />,
      featureCode: "SAFETY_OBSERVATION_MODULE",
      module: "Safety",
    },
  ];

  const filteredReports = allReports.filter((report) => {
    if (!canViewReports) return false;

    const parentTab = moduleTabsAll.find(t => t.id === report.module);
    if (parentTab && !isModuleSubscribed(parentTab.moduleCode)) return false;

    const matchesModule =
      selectedModule === "All" || report.module === selectedModule;
    return matchesModule;
  });

  return (
    <div className="reports-dashboard-container">
      <div className="reports-header-wrapper">
        <div className="reports-header">
          <h1>Reports Dashboard</h1>
          <div className="module-filter-tabs">
            {moduleTabs.map((tab) => (
              <button
                key={tab.id}
                className={`filter-tab ${
                  selectedModule === tab.id ? "active" : ""
                }`}
                onClick={() => setSelectedModule(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className="reports-controls">
          <p className="reports-helper-text">
            Select a report to view detailed analytics
          </p>
          <div className="view-mode-toggle">
            <button
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              title="Grid View"
            >
              <HiOutlineSquares2X2 />
            </button>
            <button
              className={`toggle-btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
              title="List View"
            >
              <HiOutlineListBullet />
            </button>
          </div>
        </div>
      </div>

      <div className={`reports-grid ${viewMode === "list" ? "list-view" : ""}`}>
        {filteredReports.map((report, index) => (
          <div
            key={index}
            className="report-card"
            onClick={() => navigate(report.path, { state: report.state })}
          >
            <div className="report-icon-wrapper">{report.icon}</div>
            <div className="report-info">
              <h3>{report.title}</h3>
              <p>{report.description}</p>
            </div>
          </div>
        ))}

        {filteredReports.length === 0 && (
          <div className="no-reports">
            <p>No reports found for the selected module.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsDashboard;
